import React, { useState } from 'react';
import './index.css';

export const FIELD_CONFIG = {
	displayName: { label: 'Display Name', icon: 'user' },
	fullName: { label: 'Full Name', icon: 'user' },
	name: { label: 'Name', icon: 'user' },
	givenName: { label: 'First Name', icon: 'user' },
	familyName: { label: 'Last Name', icon: 'user' },
	middleName: { label: 'Middle Name', icon: 'user' },
	email: { label: 'Email', icon: 'envelope-o' },
	emails: { label: 'Emails', icon: 'envelope-o' },
	emailAddresses: { label: 'Emails', icon: 'envelope-o' },
	phone: { label: 'Phone', icon: 'phone' },
	phones: { label: 'Phones', icon: 'phone' },
	phoneNumbers: { label: 'Phones', icon: 'phone' },
	jobTitle: { label: 'Job Title', icon: 'briefcase' },
	company: { label: 'Company', icon: 'building-o' },
	department: { label: 'Department', icon: 'sitemap' },
	address: { label: 'Address', icon: 'map-marker' },
	addresses: { label: 'Address', icon: 'map-marker' },
	postalAddresses: { label: 'Address', icon: 'map-marker' },
	note: { label: 'Note', icon: 'sticky-note-o' },
	notes: { label: 'Notes', icon: 'sticky-note-o' },
	group: { label: 'Label', icon: 'tag' },
	groups: { label: 'Labels', icon: 'tags' },
	picture: { label: 'Photo', icon: 'picture-o' },
	website: { label: 'Website', icon: 'globe' },
	birthday: { label: 'Birthday', icon: 'calendar' }
};

export const resolveLabelValue = (val, groupNames = {}, reduxLabels = []) => {
	if (!val) return '';
	const cleanId = String(val).includes(':') ? String(val).split(':')[0] : String(val);
	if (cleanId === '6') return 'My Contacts';
	if (cleanId === 'starred') return 'Starred in Android';
	if (groupNames && groupNames[cleanId]) return groupNames[cleanId];
	if (groupNames && groupNames[val]) return groupNames[val];
	const found = reduxLabels.find(l => l.groupId === cleanId || l.id === cleanId || l.groupId === val);
	if (found && (found.name || found.title)) return found.name || found.title;
	return cleanId;
};

export const formatDiffValue = (val, fieldName, groupNames = {}, reduxLabels = []) => {
	if (val === null || val === undefined || val === '') {
		return null;
	}

	if (fieldName === 'group' || fieldName === 'groups') {
		if (Array.isArray(val)) {
			const uniqueLabels = Array.from(new Set(
				val.map(v => resolveLabelValue(v, groupNames, reduxLabels)).filter(Boolean)
			));
			return uniqueLabels.join(', ');
		}
		return resolveLabelValue(val, groupNames, reduxLabels);
	}

	if (typeof val === 'string') {
		const trimmed = val.trim();
		if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
			try {
				const parsed = JSON.parse(trimmed);
				return formatDiffValue(parsed, fieldName, groupNames, reduxLabels);
			} catch (e) {
				// Keep raw string if not JSON
			}
		}
		return val;
	}

	if (Array.isArray(val)) {
		const formattedItems = val.map(item => {
			if (typeof item === 'object' && item !== null) {
				return item.value || item.number || item.address || item.formattedAddress || item.name || JSON.stringify(item);
			}
			return String(item);
		});
		return formattedItems.join(', ');
	}

	if (typeof val === 'object' && val !== null) {
		return val.value || val.number || val.address || val.formattedAddress || val.name || JSON.stringify(val);
	}

	return String(val);
};

export const getNormalizedChanges = (item) => {
	if (!item) return [];

	if (item.structuredChanges && item.structuredChanges.length > 0) {
		return item.structuredChanges;
	}

	if (item.changes && typeof item.changes === 'object') {
		return Object.entries(item.changes).map(([fieldName, diff]) => ({
			fieldName,
			action: diff?.type || (diff?.oldValue && diff?.newValue ? 'MODIFIED' : diff?.newValue ? 'ADDED' : 'REMOVED'),
			oldValue: diff?.oldValue,
			newValue: diff?.newValue
		}));
	}

	return [];
};

export default function InlineDiff({ item, groupNames = {}, reduxLabels = [], maxVisible = 3 }) {
	const [isExpanded, setIsExpanded] = useState(false);
	const rawChanges = getNormalizedChanges(item);

	// Filter out non-changes and empty-to-empty / whitespace-only transitions
	const validChanges = rawChanges.filter(c => {
		const oldStr = formatDiffValue(c.oldValue, c.fieldName, groupNames, reduxLabels);
		const newStr = formatDiffValue(c.newValue, c.fieldName, groupNames, reduxLabels);
		const cleanOld = oldStr != null ? String(oldStr).trim() : '';
		const cleanNew = newStr != null ? String(newStr).trim() : '';
		return cleanOld !== cleanNew;
	});

	if (validChanges.length === 0) {
		return null;
	}

	const visibleChanges = isExpanded ? validChanges : validChanges.slice(0, maxVisible);
	const hasMore = validChanges.length > maxVisible;

	return (
		<div className="history-inline-diff">
			{visibleChanges.map((change, idx) => {
				const config = FIELD_CONFIG[change.fieldName] || {
					label: change.fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
					icon: 'pencil'
				};

				const oldVal = formatDiffValue(change.oldValue, change.fieldName, groupNames, reduxLabels);
				const newVal = formatDiffValue(change.newValue, change.fieldName, groupNames, reduxLabels);

				const isAdded = !oldVal && Boolean(newVal);
				const isRemoved = Boolean(oldVal) && !newVal;

				return (
					<div key={idx} className="diff-item-row">
						<div className="diff-item-label" title={config.label}>
							<i className={`fa fa-${config.icon} mr-1 text-muted`} />
							<span>{config.label}</span>
						</div>

						<div className="diff-item-comparison">
							{isAdded && (
								<span className="diff-pill diff-pill--added">
									<i className="fa fa-plus mr-1" />
									{newVal}
								</span>
							)}

							{isRemoved && (
								<span className="diff-pill diff-pill--removed">
									<del>
										<i className="fa fa-minus mr-1" />
										{oldVal}
									</del>
								</span>
							)}

							{!isAdded && !isRemoved && (
								<div className="diff-item-change">
									<span className="diff-pill diff-pill--before">
										<del>{oldVal}</del>
									</span>
									<span className="diff-arrow">→</span>
									<span className="diff-pill diff-pill--after">
										<ins>{newVal}</ins>
									</span>
								</div>
							)}
						</div>
					</div>
				);
			})}

			{hasMore && (
				<button
					type="button"
					className="btn btn-link btn-xs p-0 text-primary font-weight-medium mt-1 d-inline-flex align-items-center"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} mr-1 small`} />
					{isExpanded ? 'Show fewer changes' : `Show ${validChanges.length - maxVisible} more changes`}
				</button>
			)}
		</div>
	);
}
